import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { store } from "../../../app/store";
import { resetUser } from "../../../app/userSlice";
import { get, STORAGE_KEYS } from "../../../lib/storage";
import authService from "./authService";

// Expired-token handling, layered:
//   1. silent refresh: re-exchange the Firebase session for a new API token
//   2. if that fails, end the session; AuthGuard then sends the user to /login,
//      which shows a "session expired" notice.

const SESSION_EXPIRED_KEY = "SESSION_EXPIRED";
// Refresh slightly early so a request doesn't leave with a token that expires in flight.
const EXPIRY_LEEWAY_SECONDS = 30;

let inflightRefresh: Promise<string | null> | null = null;

const readExp = (token: string): number | null => {
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1]));
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
};

export const tokenExpiresSoon = (token: string): boolean => {
  const exp = readExp(token);
  return exp !== null && exp - EXPIRY_LEEWAY_SECONDS <= Date.now() / 1000;
};

const doRefresh = async (): Promise<string | null> => {
  try {
    // Lazy: firebase/auth is only needed on this path, and importing it at
    // module load drags it into everything that imports an HTTP layer.
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    await auth.authStateReady();
    if (!auth.currentUser) return null;

    const idToken = await auth.currentUser.getIdToken(true);
    const { token } = await authService.googleLogin(idToken);
    authService.setAuthToken(token);
    return token;
  } catch (err) {
    console.warn("Silent session refresh failed:", err);
    return null;
  }
};

// Single-flight: parallel requests that all hit an expired token share one refresh.
export const refreshSession = (): Promise<string | null> => {
  if (!inflightRefresh) {
    inflightRefresh = doRefresh().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
};

export const expireSession = (): void => {
  authService.clearAuthToken();
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  } catch {
    // The notice is a nicety; the redirect below doesn't depend on it.
  }
  store.dispatch(resetUser());
};

// Read once: the notice shows on the next login screen visit only.
export const consumeSessionExpiredNotice = (): boolean => {
  try {
    const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return expired;
  } catch {
    return false;
  }
};

type RetriableConfig = InternalAxiosRequestConfig & { _sessionRetried?: boolean };

const setAuthHeader = (config: InternalAxiosRequestConfig, token: string) => {
  // AuthHeaders#set matches header names case-insensitively; the legacy
  // http_common.js layer sends both "Authorization" and "authorization".
  config.headers.set("Authorization", token);
};

export const attachSessionRefresh = (instance: AxiosInstance): void => {
  // Proactive: refresh before sending when the stored token is (nearly) expired.
  instance.interceptors.request.use(async (config) => {
    const token = get(STORAGE_KEYS.ACCESS_TOKEN);
    if (token && !authService.isDevMode() && tokenExpiresSoon(token)) {
      const fresh = await refreshSession();
      if (fresh) setAuthHeader(config, fresh);
    }
    return config;
  });

  // Reactive: the server rejected the token, so refresh once and retry.
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetriableConfig | undefined;
      const hadSession = !!get(STORAGE_KEYS.ACCESS_TOKEN);

      // Signed-out callers (e.g. public download links) have no session to rescue.
      if (
        error.response?.status !== 401 ||
        !config ||
        config._sessionRetried ||
        !hadSession ||
        authService.isDevMode()
      ) {
        return Promise.reject(error);
      }

      config._sessionRetried = true;
      const fresh = await refreshSession();
      if (!fresh) {
        expireSession();
        return Promise.reject(error);
      }

      setAuthHeader(config, fresh);
      return instance.request(config);
    }
  );
};
